/** Draws a cave run: status line, the cave, particles (docs/spec/04 §4-5). */

import { atlasFor } from '../../render/atlas';
import { remasterAtlasFor } from '../../render/remaster/atlas';
import { drawLighting } from '../../render/remaster/lighting';
import { digbyPixel, drawCave } from '../../render/caveView';
import { DARK_GREY, HIGH_CONTRAST, ORANGE, WHITE, YELLOW } from '../../render/palette';
import type { Screen } from '../../render/screen';
import { textWidth } from '../../render/screen';
import { drawPlayStatus, drawStatusText } from '../../render/statusBar';
import type { Options } from '../../platform/options';
import type { CaveRun } from './caveRun';

export const STATUS_H = 8;

/** The game's virtual width: 320, or wider to fill a wide window in Wide view (docs/spec/04 §1). */
export function gameWidth(opts: Options): number {
  if (!opts.wide) return 320;
  const aspect = window.innerWidth / Math.max(1, window.innerHeight);
  const w = Math.round((200 * aspect) / 16) * 16;
  return Math.min(432, Math.max(320, w));
}

export interface StatusLine {
  /** Replaces the numbers (intro text, OUT OF TIME, GAME OVER, PAUSED). */
  text: string | null;
  colour?: number;
  score: number;
}

export function drawRun(screen: Screen, run: CaveRun, opts: Options, status: StatusLine): void {
  const s = run.state;
  const colors = opts.highContrast ? HIGH_CONTRAST : run.built.colors;
  const remastered = opts.art === 'remastered';
  const atlas = remastered ? remasterAtlasFor(colors) : atlasFor(colors);
  run.viewW = screen.width;
  run.viewH = screen.height - STATUS_H;
  const shake = opts.shake ? run.particles.shake() : { x: 0, y: 0 };
  const camX = run.camera.x + shake.x;
  const camY = run.camera.y + shake.y;
  drawCave(screen, s, atlas, {
    top: STATUS_H,
    viewW: run.viewW,
    viewH: run.viewH,
    camX,
    camY,
    t: run.t,
    smooth: opts.smooth,
    anim: run.anim,
    cover: run.cover,
    background: run.flashMs > 0 ? WHITE : 0,
    sparkle: run.sparkleMs > 0,
  });
  if (remastered && opts.lighting) {
    const view = { top: STATUS_H, viewW: run.viewW, viewH: run.viewH, camX, camY, anim: run.anim, t: run.t, smooth: opts.smooth };
    drawLighting(screen, s, { ...view, digby: digbyPixel(s, run.t, opts.smooth) });
  }
  if (opts.particles) run.particles.draw(screen, camX, camY, STATUS_H, run.viewW, run.viewH);
  if (status.text !== null) drawStatusText(screen, status.text, status.colour ?? WHITE);
  else {
    drawPlayStatus(screen, {
      quota: s.params.diamondsNeeded,
      value: s.params.diamondValue,
      extraValue: s.params.extraValue,
      diamonds: s.diamonds,
      gateOpen: s.gateOpen,
      time: run.shownTime,
      score: status.score,
    });
  }
}

/** A framed message across the middle of the playfield: LEVEL COMPLETE, GAME OVER. */
export function drawBanner(screen: Screen, text: string, sub: string, anim: number): void {
  const w = Math.max(textWidth(text, 2), textWidth(sub)) + 32;
  const h = sub ? 46 : 34;
  const x = Math.floor((screen.width - w) / 2);
  const y = Math.floor((screen.height - h) / 2) + 4;
  screen.fill(x + 3, y + 3, w, h, 0);
  screen.fill(x, y, w, h, DARK_GREY);
  screen.box(x, y, w, h, (anim >> 2) % 2 === 0 ? YELLOW : ORANGE);
  screen.box(x + 2, y + 2, w - 4, h - 4, 0);
  screen.shadowText(text, screen.width / 2, y + 10, YELLOW, 0, 'center', 2);
  if (sub) screen.text(sub, screen.width / 2, y + 32, WHITE, 'center');
}
